import React, { useCallback, useEffect, useState } from 'react';

const Context = React.createContext({});
export const useContext = () => React.useContext(Context);

export function Provider({
  children,
  layers,
  canOpen = true,
  triggerControlChange,
  containerRef,
}) {
  const [isOpen, setOpen] = useState();
  const [visibleLegends, setVisibleLegends] = useState([]);
  const [visibleOpacities, setVisibleOpacities] = useState([]);

  useEffect(() => {
    if (!canOpen) setOpen(false);
  }, [canOpen]);

  const toggle = useCallback(
    (layer) => (currentState) => {
      if (!currentState.includes(layer.id)) return [...currentState, layer.id];
      return currentState.filter((k) => k !== layer.id);
    },
    [],
  );

  const toggleLegend = useCallback(
    (layer) => setVisibleLegends(toggle(layer)),
    [toggle],
  );

  const toggleOpacity = useCallback(
    (layer) => setVisibleOpacities(toggle(layer)),
    [toggle],
  );

  const changeOpacity = useCallback(
    (layer, value) => layers.updateStyle(layer, { fillOpacity: value / 100 }),
    [layers],
  );

  const buildLayerName = useCallback(
    (layer) => {
      const builder = layer.buildLayerLabel || (() => layer.name);
      return layers.isVisible(layer) ? builder(layer) : layer.name;
    },
    [layers],
  );

  const toggleOpen = useCallback(() => {
    const shouldOpen = !isOpen;
    if (shouldOpen && triggerControlChange) triggerControlChange();
    setOpen(shouldOpen);
  }, [isOpen, triggerControlChange]);

  const contextState = React.useMemo(
    () => ({
      layers,
      isOpen,
      setOpen,
      visibleLegends,
      visibleOpacities,
      toggleLegend,
      toggleOpacity,
      changeOpacity,
      buildLayerName,
      toggleOpen,
      containerRef,
    }),
    [
      isOpen,
      layers,
      visibleLegends,
      visibleOpacities,
      toggleLegend,
      toggleOpacity,
      changeOpacity,
      buildLayerName,
      toggleOpen,
      containerRef,
    ],
  );

  return <Context.Provider value={contextState}>{children}</Context.Provider>;
}
